import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import {
  isTerminal,
  percentDone,
  retryableRows,
} from '@platform/schema/generation';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { useJobActions } from '../authoring/GenerationJobs';
import { JobNow } from '../authoring/JobNow';
import { useNow } from '../authoring/useNow';
import { jobQuery } from '../library/sources';

import type { AlertColor } from '@mui/material/Alert';
import type { JobStatus } from '@platform/schema/generation';

/** While a build runs: the table and these bars ask again this often. */
export const POLL_MS = 3_000;

/** A failed rebuild leaves its row reading Built - draft, so this bar is where it shows. */
const SEVERITY: Partial<Record<JobStatus, AlertColor>> = {
  SUCCEEDED: 'success',
  PARTIAL: 'warning',
  FAILED: 'error',
};

/**
 * Each running build's progress, with Cancel remaining — CURR-22's locked proposal: the
 * queued lessons are taken out and the one being written finishes.
 */
export function BuildProgress({ jobIds }: { jobIds: readonly string[] }) {
  if (jobIds.length === 0) return null;
  return (
    <Stack spacing={1}>
      {jobIds.map((id) => (
        <Batch key={id} id={id} />
      ))}
    </Stack>
  );
}

function Batch({ id }: { id: string }) {
  const job = useQuery({
    ...jobQuery(id),
    refetchInterval: (query) =>
      query.state.data !== undefined && isTerminal(query.state.data.status)
        ? false
        : POLL_MS,
  });
  const { retryAll: retry, cancel } = useJobActions(id);

  const detail = job.data;
  const finished = detail !== undefined && isTerminal(detail.status);
  const now = useNow(detail !== undefined && !finished);
  if (detail === undefined) return null;
  const { lessons } = detail;

  return (
    <Alert
      severity={SEVERITY[detail.status] ?? 'info'}
      action={
        <Stack direction="row" spacing={1}>
          {!finished && lessons.pending > 0 && (
            <Button
              size="small"
              color="inherit"
              disabled={cancel.isPending}
              onClick={() => {
                cancel.mutate();
              }}
            >
              Cancel remaining
            </Button>
          )}
          {retryableRows(lessons) > 0 && (
            <Button
              size="small"
              color="inherit"
              disabled={retry.isPending}
              onClick={() => {
                retry.mutate();
              }}
            >
              Retry failed
            </Button>
          )}
          <Link
            component={RouterLink}
            to={`/authoring/jobs/${id}`}
            variant="body2"
          >
            Open job
          </Link>
        </Stack>
      }
    >
      <Stack spacing={1} sx={{ minWidth: 320 }}>
        <LinearProgress
          variant="determinate"
          value={percentDone(lessons)}
          aria-label={`${detail.label} progress`}
        />
        <JobNow job={detail} now={now} />
      </Stack>
    </Alert>
  );
}
